import React, { useState } from 'react';
import styled from 'styled-components';
import { Card } from '../../design-system/components/Card';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { typography } from '../../design-system/tokens/typography';
import { animations } from '../../design-system/tokens/animations';
import { XMarkIcon, ExclamationTriangleIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const NotificationContainer = styled(Card)<{ type: 'error' | 'warning' | 'info' | 'success' }>`
  padding: ${spacing[5]};
  border-left: 4px solid ${props => {
    switch (props.type) {
      case 'error':
        return props.theme.colors.error;
      case 'warning':
        return props.theme.colors.warning;
      case 'success':
        return props.theme.colors.success;
      default:
        return props.theme.colors.primary;
    }
  }};
  position: relative;
  animation: ${animations.transition(['opacity', 'transform'], animations.duration.normal, animations.easing.easeOut)};
  
  ${props => props.type === 'error' && `
    background: ${props.theme.colors.error}15;
  `}
  ${props => props.type === 'warning' && `
    background: ${props.theme.colors.warning}15;
  `}
  ${props => props.type === 'info' && `
    background: ${props.theme.colors.primary}15;
  `}
  ${props => props.type === 'success' && `
    background: ${props.theme.colors.success}15;
  `}
`;

const NotificationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing[3]};
`;

const HeadingWithMarginBottom = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const NotificationContent = styled.div`
  display: flex;
  gap: ${spacing[3]};
  align-items: flex-start;
`;

const IconWrapper = styled.div<{ type: 'error' | 'warning' | 'info' | 'success' }>`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  color: ${props => {
    switch (props.type) {
      case 'error':
        return props.theme.colors.error;
      case 'warning':
        return props.theme.colors.warning;
      case 'success':
        return props.theme.colors.success;
      default:
        return props.theme.colors.primary;
    }
  }};
`;

const TextContent = styled.div`
  flex: 1;
`;

const DismissButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${spacing[1]};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${animations.transition(['color'], animations.duration.fast, animations.easing.easeOut)};
  
  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const ActionButton = styled(Button)`
  margin-top: ${spacing[3]};
`;

export interface NotificationCardProps {
  type: 'error' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  dismissible?: boolean;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  type,
  title,
  message,
  action,
  onDismiss,
  dismissible = true,
}) => {
  const [dismissed, setDismissed] = useState(false);

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) {
      setTimeout(onDismiss, 300); // Wait for animation
    }
  };

  if (dismissed) {
    return null;
  }

  const getIcon = () => {
    switch (type) {
      case 'error':
        return <ExclamationTriangleIcon />;
      case 'warning':
        return <ExclamationTriangleIcon />;
      case 'success':
        return <CheckCircleIcon />;
      default:
        return <InformationCircleIcon />;
    }
  };

  return (
    <NotificationContainer type={type} variant="filled">
      <NotificationHeader>
        <NotificationContent>
          <IconWrapper type={type}>
            {getIcon()}
          </IconWrapper>
          <TextContent>
            <HeadingWithMarginBottom variant="h5" color="text">
              {title}
            </HeadingWithMarginBottom>
            <Text size="sm" color="secondary">
              {message}
            </Text>
            {action && (
              <ActionButton
                variant="outline"
                size="sm"
                onClick={action.onClick}
              >
                {action.label}
              </ActionButton>
            )}
          </TextContent>
        </NotificationContent>
        {dismissible && (
          <DismissButton onClick={handleDismiss} aria-label="Dismiss">
            <XMarkIcon style={{ width: '20px', height: '20px' }} />
          </DismissButton>
        )}
      </NotificationHeader>
    </NotificationContainer>
  );
};

export default NotificationCard;
